<script setup lang="ts">
  import ColorPicker from '@/components/form-wizard/ColorPicker.vue'
  import { isHexColor } from '@/utils/portal-theme'
  import { Cross } from '@logistiek-zonder-papier/lzp-library'
  import { computed, useId } from 'vue'

  const model = defineModel<string | null>({ required: true })

  const { label, defaultColor, defaultLabel } = defineProps<{
    label: string
    defaultColor: string
    defaultLabel: string
  }>()

  const id = useId()

  const error = computed<string | null>(() =>
    model.value == null || isHexColor(model.value) ? null : 'Gebruik 6 tekens, zoals #0074c2'
  )

  const swatchColor = computed<string>(() => (isHexColor(model.value) ? model.value : defaultColor))

  function normalize(value: string): string | null {
    const trimmed = value.trim().toLowerCase()
    if (trimmed === '') return null
    return /^[0-9a-f]{6}$/.test(trimmed) ? `#${trimmed}` : trimmed
  }

  function onTextInput(event: Event) {
    model.value = normalize((event.target as HTMLInputElement).value)
  }

  function onPick(value: string | undefined) {
    if (value) model.value = value.toLowerCase()
  }
</script>

<template>
  <div class="color-field">
    <label class="label" :for="id">{{ label }}</label>

    <div class="control" :class="{ invalid: error }">
      <ColorPicker class="swatch" :model-value="swatchColor" @update:model-value="onPick" />

      <input
        :id="id"
        class="hex"
        type="text"
        maxlength="7"
        spellcheck="false"
        autocomplete="off"
        :value="model ?? ''"
        :placeholder="defaultLabel"
        :aria-invalid="!!error"
        @input="onTextInput"
      />

      <button
        v-if="model != null"
        type="button"
        class="clear"
        :title="`${defaultLabel} gebruiken`"
        :aria-label="`${defaultLabel} gebruiken`"
        @click="model = null"
      >
        <Cross class="clear-icon" />
      </button>
    </div>

    <span v-if="error" class="error">{{ error }}</span>
  </div>
</template>

<style scoped lang="css">
  .color-field {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
    min-width: 0;
  }

  .label {
    color: var(--colours-grey-5);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
  }

  .control {
    display: flex;
    align-items: center;
    gap: var(--spacing-xs);
    height: var(--components-component-height);
    padding: 0 var(--spacing-xs);
    border: 1px solid var(--colours-grey-3);
    border-radius: var(--components-form-field-corners);
    background: var(--colours-grey-1);
    transition: border-color 120ms ease-in-out;
  }

  .control:hover {
    border-color: color-mix(in srgb, var(--colours-grey-3) 60%, var(--colours-grey-4) 40%);
  }

  .control:focus-within {
    border-color: transparent;
    box-shadow: 0 0 0 2px var(--colours-support-keyboard-focus);
  }

  .control.invalid {
    border-color: var(--colours-support-negative);
  }

  .swatch {
    flex-shrink: 0;
  }

  .hex {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    color: var(--colours-grey-5);
    font: inherit;
    font-size: 1rem;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
    text-transform: uppercase;
  }

  .hex::placeholder {
    color: var(--colours-grey-4);
    text-transform: none;
    letter-spacing: normal;
  }

  .clear {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: var(--colours-grey-4);
    cursor: pointer;
  }

  .clear:hover {
    background: var(--colours-support-hover);
    color: var(--colours-grey-5);
  }

  .clear:focus-visible {
    outline: 2px solid var(--colours-support-keyboard-focus);
  }

  .clear-icon {
    width: 1rem;
    height: 1rem;
  }

  .error {
    color: var(--colours-support-negative);
    font-size: 0.8125rem;
    line-height: 1.25rem;
  }
</style>
